/**
 * Site menu — the header's MENU disclosure.
 *
 * GNG's navigation is a single vertical list at EVERY width, opened from a MENU
 * trigger in the bar. It replaces two things the kit shipped: the horizontal row
 * of links above 900px, and the sliding `mobileNav` drawer below it. Those two
 * had to agree on a breakpoint and twice did not — see the 900px note in
 * components/interactive/mobile-nav. One list cannot disagree with itself.
 *
 * STATE LIVES IN THE STORE, NOT HERE. The panel renders inside `.site-header`,
 * which is outside `#swup`, so Alpine never tears it down and this factory runs
 * once for the document's lifetime. `$store.nav.menuOpen` is what router/swup.js
 * closes on `visit:start`; a component-local flag would be invisible to it.
 *
 * The kit's `mobileNav` reads `$store.nav.mobileOpen` from the same store and is
 * still registered — it is library tier and unrendered here, not deleted.
 */
export default function siteMenu() {
    return {
        init() {
            this.watchSheet();
        },

        get isOpen() {
            return this.$store.nav.menuOpen;
        },

        get isClosed() {
            return !this.$store.nav.menuOpen;
        },

        get expandedAttr() {
            return String(this.$store.nav.menuOpen);
        },

        open() {
            this.$store.nav.menuOpen = true;
        },

        /**
         * Close the panel.
         *
         * THE ONE EVERY OTHER CLOSE PATH GOES THROUGH — `@click.outside`, the
         * scroll dismissal and Escape all reach the store from here.
         *
         * IT WAS NEVER WRITTEN. The disclosure shipped on 1 Sep 2026 with three
         * call sites and a template binding referring to a method the factory
         * did not define, so `@click.outside="close"` raised `Undefined
         * variable: close` and `closeOnScroll`/`closeAndFocus` both threw
         * `this.close is not a function` — to the console, never to the screen.
         * The only thing that actually closed the menu was the trigger's own
         * `toggle()`, plus `router/swup.js` writing the store on `visit:start`,
         * and a navigation closing it is what made the failure look
         * intermittent: click a link with the menu open and it closed, click
         * the page and it did not.
         */
        close() {
            this.$store.nav.menuOpen = false;
        },

        /**
         * Keep `--menu-sheet-height` equal to the panel's height. That variable
         * sizes `.menu-sheet` — the fixed, blurred surface the list sits on.
         *
         * IT LIVES OUTSIDE THE HEADER AND SO CANNOT BE SIZED BY CSS. The panel
         * is two CMS-driven lists and the taller one sets the height, so there
         * is no expression to write; and the sheet cannot be a descendant of
         * the panel, because a backdrop-filter inside the header samples
         * nothing (the header is a backdrop root) and a full-bleed
         * pseudo-element inflates the document. See components/_chrome.css.
         *
         * A ResizeObserver RATHER THAN A MEASUREMENT ON OPEN, because on open
         * there is nothing to measure yet: the panel is `x-show`n, so at the
         * moment the click handler runs it is still `display: none` and every
         * box is zero — which is exactly what a first attempt wrote, a sheet
         * 0px tall. The observer fires when the panel actually gets a box,
         * whenever that is, and again if an editor renames an item into a
         * second line. Zero heights are ignored so closing does not wipe the
         * value mid-fade.
         *
         * If any of this fails the variable stays unset, the sheet is 0 tall,
         * and the menu degrades to a list over the page rather than breaking.
         *
         * @returns {void}
         */
        watchSheet() {
            const panel = this.$refs.panel;

            if (!panel || typeof ResizeObserver === 'undefined') {
                return;
            }

            new ResizeObserver(() => {
                const height = Math.ceil(panel.getBoundingClientRect().height);

                if (height > 0) {
                    document.documentElement.style.setProperty('--menu-sheet-height', `${height}px`);
                }
            }).observe(panel);
        },

        toggle() {
            this.$store.nav.menuOpen = !this.$store.nav.menuOpen;
        },

        /**
         * Escape closes the panel AND returns the caret to the trigger.
         *
         * The panel is a disclosure, not a modal — it takes no focus trap, so
         * focus is wherever the reader left it. Closing without moving focus
         * would strand it on a link that is now `display: none`, and the browser
         * then drops it to <body>: the next Tab restarts at the top of the page.
         *
         * BOUND ON THE WINDOW, which is why the early return is here rather than
         * implied. Bound on the component's own root it never fired for a reader
         * who had opened the panel by TAP: a touch click leaves focus on <body>
         * in Chromium, so the keydown had no path through the root and Escape did
         * nothing at all on a phone. Listening globally fixes that and costs one
         * guard — without it, Escape anywhere on a page with a CLOSED menu would
         * yank focus up into the header.
         */
        /**
         * Scrolling dismisses the panel.
         *
         * The header hides on scroll down and takes the panel with it, so an
         * open menu would ride off the top of the window and come back on the
         * way up — still open, over whatever the reader had scrolled to. It also
         * settles what a reader means by scrolling with a menu open, which is
         * "I am done with this".
         *
         * Focus is NOT moved here, unlike on Escape: a scroll is a pointer or
         * wheel gesture, and yanking the caret into the header would be a jump
         * nobody asked for. The guard keeps this off the reactive store on every
         * frame of every scroll on every page.
         */
        closeOnScroll() {
            if (this.$store.nav.menuOpen) {
                this.close();
            }
        },

        closeAndFocus() {
            if (!this.$store.nav.menuOpen) {
                return;
            }

            this.close();
            this.$refs.trigger?.focus();
        },
    };
}
